import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  forwardRef,
  ViewEncapsulation,
  ViewChild,
  ElementRef,
  OnDestroy,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormControl,
  ValidationErrors,
  Validator,
  NG_VALIDATORS,
} from '@angular/forms';
import { distinctUntilChanged, fromEvent, map, Subscription } from 'rxjs';
import { TDateFormatTypes } from '../../models/date-picker-date-format.types';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { HttpClient } from '@angular/common/http';

export type EditableInputType =
  | 'text'
  | 'date'
  | 'dropdown'
  | 'time'
  | 'number';

export interface EditableInputValueChange {
  status: boolean;
  previousValue: string | null;
  newValue: string | null;
  datasetValueName?: string;
  datasetValueCode?: string;
}

@Component({
  selector: 'irembogov-editable-input',
  templateUrl: './irembo-editable-input.component.html',
  styleUrls: ['./irembo-editable-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => IremboEditableInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => IremboEditableInputComponent),
      multi: true,
    },
  ],
})
export class IremboEditableInputComponent
  implements
    ControlValueAccessor,
    Validator,
    OnDestroy,
    AfterViewInit,
    OnChanges
{
  @Input() placeholder = '';
  @Input() id?: string | number | (string | number)[];
  @Input() initialValue?: string | EditableInputValueChange;
  @Input() label?: string;
  @Input() inputType: EditableInputType = 'text';
  @Input() textType: 'text' | 'number' | 'tel' | 'email' | 'password' = 'text';
  @Input() minNumber?: number;
  @Input() maxNumber?: number;
  @Input() minLength?: number;
  @Input() maxLength?: number;
  @Input() pattern?: string | RegExp;
  @Input() dateFormat: TDateFormatTypes = 'DD MM YYYY';
  @Input() minDate: NgbDateStruct = { year: 1900, month: 1, day: 1 };
  @Input() maxDate: NgbDateStruct = { year: 2100, month: 12, day: 31 };
  @Input() delimiter = '/';
  @Input() addRemoveTimeMinDate?: string;
  @Input() addRemoveTimeMaxDate?: string;
  @Input() items: Record<string, unknown>[] = [];
  @Input() bindLabel = 'label';
  @Input() bindValue = 'value';
  @Input() multiple = false;
  @Input() dataset: any;
  @Input() useBaseUrl = true;

  @Input() set options(val: Record<string, unknown>[]) {
    if (val && Array.isArray(val)) {
      this.items = val;
    }
  }

  @Output() valueChange = new EventEmitter<EditableInputValueChange>();

  isEditing = false;
  isEdited = false;
  displayFormControl = new FormControl<string | null>(null);
  @Input() customFormControl = new FormControl();

  private valueObject: EditableInputValueChange = {
    status: false,
    previousValue: '',
    newValue: '',
  };

  private tempValue: string | null = null;
  private editableInputSub: Subscription | undefined;
  @ViewChild('editableInput') private readonly editableInputEl:
    | ElementRef
    | undefined;

  @ViewChild('dropdownWrapper', { static: false }) private dropdownWrapper:
    | ElementRef
    | undefined;

  /* eslint-disable @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function*/
  private _onChange = (_: any) => {};
  private _onTouch = (_: any) => {};
  private _onValidationChange = () => {};
  /* eslint-enable */

  constructor(
    private readonly cd: ChangeDetectorRef,
    private readonly http: HttpClient
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialValue'] && this.initialValue) {
      this.processInitialValue();
    }

    if (changes['items'] && this.items && this.inputType === 'dropdown') {
      this.updateDropdownValues();
    }
  }

  ngAfterViewInit(): void {
    this.initializeFormControl();
    this.setupTextInputEvents();

    // For dropdowns, ensure disabled state is set before adjusting UI
    if (this.inputType === 'dropdown' || this.inputType === 'time') {
      this.enforceDropdownDisabledState();
    }

    this.adjustInputTypeUI();
    this.cd.detectChanges();
  }

  ngOnDestroy(): void {
    if (this.editableInputSub) {
      this.editableInputSub.unsubscribe();
    }
  }

  startEdit(): void {
    this.isEditing = true;
    this.tempValue = this.valueObject.newValue;
    this.displayFormControl.markAsTouched();
    this._onValidationChange();

    this.updateUIForEditing(true);
    this.cd.detectChanges();
  }

  cancelEdit(): void {
    this.isEditing = false;
    if (this.tempValue !== null) {
      this.displayFormControl.setValue(this.tempValue || null);
    }
    this._onValidationChange();

    this.updateUIForEditing(false);
    this.cd.detectChanges();
  }

  resetToInitial(): void {
    this.isEditing = false;
    this.isEdited = false;

    let initialVal = null;
    if (
      typeof this.initialValue === 'object' &&
      'previousValue' in this.initialValue
    ) {
      initialVal = (this.initialValue as EditableInputValueChange)
        .previousValue;
    } else if (typeof this.initialValue === 'string') {
      initialVal = this.initialValue;
    } else if (this.valueObject.previousValue !== undefined) {
      initialVal = this.valueObject.previousValue;
    }

    let displayValue = initialVal;
    if (this.inputType === 'date' && initialVal) {
      displayValue = this.formatDateIfNeeded(initialVal);
    }

    this.displayFormControl.setValue(displayValue || null);

    this.valueObject = {
      status: false,
      previousValue: initialVal,
      newValue: displayValue,
    };

    if (this.inputType === 'dropdown' && this.items.length > 0 && initialVal) {
      this.setDropdownDatasetInfo(initialVal);
    }

    this.updateCustomFormControl();

    this.customFormControl.setErrors(null);

    this.emitValueChange();

    this.updateUIForEditing(false);
    this.cd.detectChanges();
  }

  saveEdit(): void {
    // Existing text validation
    if (this.inputType === 'text' && !this.displayFormControl.value?.trim()) {
      this.displayFormControl.markAsTouched();
      this._onValidationChange();
      return;
    }

    if (this.inputType === 'dropdown' && !this.displayFormControl.value) {
      this.displayFormControl.markAsTouched();
      this._onValidationChange();
      return;
    }

    if (this.inputType === 'time' && !this.displayFormControl.value) {
      this.displayFormControl.markAsTouched();
      this._onValidationChange();
      return;
    }

    const errors = this.validate(
      new FormControl(this.displayFormControl.value)
    );
    if (errors) {
      this.customFormControl.setErrors(errors);
      this._onValidationChange();
      return;
    }

    this.isEditing = false;
    const newValue = this.displayFormControl.value;
    this.tempValue = null;

    let previousValue = null;
    if (
      typeof this.initialValue === 'object' &&
      'previousValue' in this.initialValue
    ) {
      previousValue = (this.initialValue as EditableInputValueChange)
        .previousValue;
    } else if (typeof this.initialValue === 'string') {
      previousValue = this.initialValue;
    } else if (this.valueObject.previousValue !== undefined) {
      previousValue = this.valueObject.previousValue;
    }

    this.isEdited = newValue !== previousValue;

    this.valueObject = {
      status: this.isEdited,
      previousValue,
      newValue,
      datasetValueName: this.valueObject.datasetValueName,
      datasetValueCode: this.valueObject.datasetValueCode,
    };

    if (this.inputType === 'dropdown' && this.items.length > 0 && newValue) {
      this.setDropdownDatasetInfo(newValue);
    }

    this.updateCustomFormControl();
    this.emitValueChange();

    this.updateUIForEditing(false);
    this.cd.detectChanges();
  }

  onDateValueChange(date: any): void {
    if (this.isEditing) {
      const formattedDate = this.formatDateIfNeeded(date);
      this.displayFormControl.setValue(formattedDate);
      this.cd.detectChanges();
    }
  }

  onDropdownValueChange(value: any): void {
    if (this.isEditing) {
      this.displayFormControl.setValue(value);
      this.cd.detectChanges();
    }
  }

  enforceNumberConstraints(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input) return;

    let value = input.value;

    if (this.textType === 'number') {
      value = value.replace(/[^\d.-]/g, '');

      if (value) {
        const numValue = parseFloat(value);

        if (
          !isNaN(numValue) &&
          this.maxNumber !== undefined &&
          numValue > this.maxNumber
        ) {
          value = this.maxNumber.toString();
        }
      }

      if (input.value !== value) {
        input.value = value;
        this.displayFormControl.setValue(value);
      }
    }

    if (this.maxLength !== undefined && value.length > this.maxLength) {
      value = value.substring(0, this.maxLength);
      input.value = value;
      this.displayFormControl.setValue(value);
    }
  }

  formatDateIfNeeded(value: string): string {
    if (!value) return value;

    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return this.formatIsoDate(value);
    }

    try {
      if (this.hasDateDelimiters(value)) {
        return this.formatDelimitedDate(value);
      }
    } catch (e) {
      // Error handling silently
    }

    return value;
  }

  validate(control: FormControl): ValidationErrors | null {
    const validationErrors: ValidationErrors = {};

    let valueToValidate: any = '';
    if (
      control.value &&
      typeof control.value === 'object' &&
      'newValue' in control.value
    ) {
      valueToValidate = (control.value as EditableInputValueChange).newValue;
    } else if (typeof control.value === 'string') {
      valueToValidate = control.value;
    }

    if (
      this.inputType === 'text' &&
      !valueToValidate?.trim?.() &&
      (this.isEditing || this.isEdited)
    ) {
      validationErrors['required'] = true;
    }

    this.validateNumberConstraints(valueToValidate, validationErrors);
    this.validateStringLength(valueToValidate, validationErrors);
    this.validatePattern(valueToValidate, validationErrors);

    return Object.keys(validationErrors).length > 0 ? validationErrors : null;
  }

  writeValue(value: EditableInputValueChange | string | null): void {
    if (value === null || value === undefined) return;

    if (typeof value === 'object' && 'newValue' in value) {
      this.valueObject = { ...(value as EditableInputValueChange) };
      this.displayFormControl.setValue(this.valueObject.newValue || '');
      this.isEdited = this.valueObject.status;
    } else if (typeof value === 'string' || typeof value === 'number') {
      const stringValue = String(value);
      this.valueObject = {
        status: false,
        previousValue: stringValue,
        newValue: stringValue,
      };
      this.displayFormControl.setValue(stringValue || '');

      if (
        this.inputType === 'dropdown' &&
        this.items.length > 0 &&
        stringValue
      ) {
        this.setDropdownDatasetInfo(stringValue);
      }

      this.customFormControl.setValue(this.valueObject, { emitEvent: false });
    }

    this.cd.detectChanges();
  }

  registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouch = fn;
  }

  registerOnValidatorChange(fn: () => void): void {
    this._onValidationChange = fn;
  }

  private processInitialValue(): void {
    if (!this.initialValue) return;

    if (
      typeof this.initialValue === 'object' &&
      'newValue' in this.initialValue
    ) {
      const valueObj = this.initialValue as EditableInputValueChange;
      this.valueObject = { ...valueObj };
      this.displayFormControl.setValue(valueObj.newValue || '');
      this.isEdited = valueObj.status;
    } else {
      const stringValue = this.initialValue as string;
      this.valueObject = {
        status: false,
        previousValue: stringValue,
        newValue: stringValue,
      };
      this.displayFormControl.setValue(stringValue || '');

      if (
        this.inputType === 'dropdown' &&
        this.items.length > 0 &&
        stringValue
      ) {
        this.setDropdownDatasetInfo(stringValue);
      }
    }

    this.updateCustomFormControl();
    this.cd.detectChanges();
  }

  private initializeFormControl(): void {
    if (this.initialValue) {
      this.processInitialValue();
    } else if (this.customFormControl.value) {
      this.handleExistingFormControlValue();
    }
  }

  private handleExistingFormControlValue(): void {
    const value = this.customFormControl.value;
    if (!value) return;

    if (typeof value === 'object' && value && 'newValue' in value) {
      this.valueObject = { ...value };
      this.displayFormControl.setValue(this.valueObject.newValue || '');
      this.isEdited = this.valueObject.status;
    } else if (typeof value === 'string' || typeof value === 'number') {
      const stringValue = String(value);
      this.valueObject = {
        status: false,
        previousValue: stringValue,
        newValue: stringValue,
      };
      this.displayFormControl.setValue(stringValue);

      if (
        this.inputType === 'dropdown' &&
        this.items.length > 0 &&
        stringValue
      ) {
        this.setDropdownDatasetInfo(stringValue);
      }

      this.updateCustomFormControl();
    }
  }

  private setupTextInputEvents(): void {
    if (this.inputType === 'text' && this.editableInputEl?.nativeElement) {
      this.editableInputSub = fromEvent(
        this.editableInputEl.nativeElement,
        'keyup'
      )
        .pipe(
          map((event: unknown) => {
            if (event instanceof Event) {
              const target = event.target as HTMLInputElement;
              return target?.value || '';
            }
            return '';
          }),
          distinctUntilChanged()
        )
        .subscribe({
          next: () => {
            this.displayFormControl.markAsTouched();
            this.cd.detectChanges();
          },
        });
    }
  }

  private adjustInputTypeUI(): void {
    if (this.inputType === 'date') {
      this.adjustDatePickerUI();
    } else if (this.inputType === 'dropdown' || this.inputType === 'time') {
      this.adjustDropdownUI();
    } else {
      // For text and number, ensure UI is updated
      if (this.displayFormControl.value || this.isEdited) {
        this.cd.detectChanges();
      }
    }
  }

  private adjustDatePickerUI(): void {
    if (this.displayFormControl.value) {
      const formattedDate = this.formatDateIfNeeded(
        this.displayFormControl.value
      );
      this.displayFormControl.setValue(formattedDate);
      this.valueObject.newValue = formattedDate;
      this.updateCustomFormControl();
    }

    setTimeout(() => {
      const calendarButtons = document.querySelectorAll('.btn-calendar');
      calendarButtons.forEach(btn => {
        const element = btn as HTMLElement;
        if (element) {
          element.style.display = 'none';
          element.style.visibility = 'hidden';
          element.style.opacity = '0';
          element.style.pointerEvents = 'none';
        }
      });
      this.cd.detectChanges();
    }, 100);
  }

  private adjustDropdownUI(): void {
    // Ensure the dropdown starts in the correct disabled state
    this.enforceDropdownDisabledState();

    setTimeout(() => {
      const selectContainers = document.querySelectorAll(
        '.custom-dropdown-wrapper .ng-select-container'
      );
      selectContainers.forEach(container => {
        const element = container as HTMLElement;
        if (element) {
          element.style.border = 'none';
          element.style.boxShadow = 'none';
          element.style.outline = 'none';
        }
      });

      const clearButtons = document.querySelectorAll(
        '.custom-dropdown-wrapper .ng-clear-wrapper'
      );
      clearButtons.forEach(btn => {
        const element = btn as HTMLElement;
        if (element) {
          element.style.display = 'none';
          element.style.visibility = 'hidden';
          element.style.opacity = '0';
          element.style.pointerEvents = 'none';
        }
      });

      // Apply the correct disabled state after styling
      this.enforceDropdownDisabledState();

      const ngSelectElements = document.querySelectorAll(
        '.custom-dropdown-wrapper ng-select'
      );
      ngSelectElements.forEach(element => {
        // Force the ng-select to update its visual state
        if (this.displayFormControl.value) {
          const event = new Event('change', { bubbles: true });
          element.dispatchEvent(event);
        }
      });
    }, 100);
  }

  private enforceDropdownDisabledState(): void {
    if (this.inputType === 'dropdown' || this.inputType === 'time') {
      const ngSelectElements = document.querySelectorAll(
        '.custom-dropdown-wrapper ng-select'
      );
      ngSelectElements.forEach(element => {
        if (!this.isEditing) {
          element.setAttribute('disabled', 'true');
          (element as HTMLElement).style.pointerEvents = 'none';
          // Also disable any input elements inside
          const inputs = element.querySelectorAll('input, button');
          inputs.forEach(input => {
            (input as HTMLElement).setAttribute('disabled', 'true');
            (input as HTMLElement).style.pointerEvents = 'none';
          });
        } else {
          element.setAttribute('disabled', 'false');
          element.removeAttribute('disabled');
          (element as HTMLElement).style.pointerEvents = 'all';
          // Re-enable input elements inside
          const inputs = element.querySelectorAll('input, button');
          inputs.forEach(input => {
            (input as HTMLElement).removeAttribute('disabled');
            (input as HTMLElement).style.pointerEvents = 'all';
          });
        }
      });

      const dropdownWrapper = document.querySelector(
        '.custom-dropdown-wrapper'
      );
      if (dropdownWrapper) {
        if (this.isEditing) {
          dropdownWrapper.classList.add('editing-mode');
        } else {
          dropdownWrapper.classList.remove('editing-mode');
        }
      }
    }
  }

  private updateDropdownValues(): void {
    if (this.inputType === 'dropdown' && this.valueObject.newValue) {
      this.setDropdownDatasetInfo(this.valueObject.newValue);
      this.updateCustomFormControl();
    }
  }

  private setDropdownDatasetInfo(value: string): void {
    if (value === null || value === undefined) return;

    const item = this.items.find(
      item =>
        item?.[this.bindValue] === value || item?.[this.bindLabel] === value
    );

    if (item) {
      this.valueObject.datasetValueName = item[this.bindLabel] as string;
      this.valueObject.datasetValueCode = item[this.bindValue] as string;

      if (item[this.bindLabel] === value && item[this.bindValue] !== value) {
        this.valueObject.newValue = item[this.bindValue] as string;
        this.displayFormControl.setValue(this.valueObject.newValue);
      }
    }
  }

  private updateCustomFormControl(): void {
    this.customFormControl.setValue(this.valueObject, { emitEvent: false });
    this._onChange(this.valueObject);
    this._onTouch(this.valueObject);
  }

  private validateNumberConstraints(
    value: string,
    errors: ValidationErrors
  ): void {
    if (
      this.textType === 'number' &&
      value !== null &&
      value !== undefined &&
      value !== ''
    ) {
      const numValue = parseFloat(value);

      if (isNaN(numValue)) {
        return;
      }

      if (this.minNumber !== undefined && numValue < this.minNumber) {
        errors['minNumber'] = {
          required: this.minNumber,
          actual: numValue,
        };
      }

      if (this.maxNumber !== undefined && numValue > this.maxNumber) {
        errors['maxNumber'] = {
          required: this.maxNumber,
          actual: numValue,
        };
      }
    }
  }

  private validateStringLength(value: string, errors: ValidationErrors): void {
    if (typeof value !== 'string') {
      return;
    }

    if (this.minLength !== undefined && value.length < this.minLength) {
      errors['minlength'] = {
        requiredLength: this.minLength,
        actualLength: value.length,
      };
    }

    if (this.maxLength !== undefined && value.length > this.maxLength) {
      errors['maxlength'] = {
        requiredLength: this.maxLength,
        actualLength: value.length,
      };
    }
  }

  private validatePattern(value: string, errors: ValidationErrors): void {
    if (!this.pattern || !value || typeof value !== 'string') {
      return;
    }

    try {
      const regex = new RegExp(this.pattern);
      if (!regex.test(value)) {
        errors['pattern'] = {
          requiredPattern: this.pattern,
          actualValue: value,
        };
      }
    } catch (error) {
      console.error(
        'Invalid regex pattern:',
        error instanceof Error ? error.message : 'unknown error'
      );
    }
  }

  private emitValueChange(): void {
    this.valueChange.emit(this.valueObject);
    this._onValidationChange();
  }

  private hasDateDelimiters(value: string): boolean {
    return value.includes('/') || value.includes('-') || value.includes('.');
  }

  private getDateDelimiter(value: string): string {
    return value.includes('/') ? '/' : value.includes('-') ? '-' : '.';
  }

  private formatIsoDate(value: string): string {
    const [year, month, day] = value.split('-').map(part => parseInt(part, 10));
    return this.formatDateParts(day, month, year);
  }

  private formatDelimitedDate(value: string): string {
    const delimiter = this.getDateDelimiter(value);
    const parts = value.split(delimiter);

    if (parts.length !== 3) {
      return value;
    }

    if (parts[0].length === 4 && /^\d{4}$/.test(parts[0])) {
      const [year, month, day] = parts;
      return this.formatDateParts(
        parseInt(day, 10),
        parseInt(month, 10),
        parseInt(year, 10)
      );
    }

    if (parts[2].length === 4 && /^\d{4}$/.test(parts[2])) {
      const [day, month, year] = parts;
      return this.formatDateParts(
        parseInt(day, 10),
        parseInt(month, 10),
        parseInt(year, 10)
      );
    }

    return value;
  }

  private formatDateParts(day: number, month: number, year: number): string {
    const paddedDay = day < 10 ? '0' + day : String(day);
    const paddedMonth = month < 10 ? '0' + month : String(month);
    return `${paddedDay}${this.delimiter}${paddedMonth}${this.delimiter}${year}`;
  }

  private updateUIForEditing(isEnabled: boolean): void {
    if (this.inputType === 'dropdown' || this.inputType === 'time') {
      this.enforceDropdownDisabledState();

      setTimeout(() => {
        this.enforceDropdownDisabledState();
        this.cd.detectChanges();
      }, 10);
    }
  }
}
